/**
 * BulkDispositionModal — apply the same disposition code to N leads.
 * V2.1.1 iter183.
 *
 * Kept intentionally simple — presents a compact grid of the 8 CRM
 * codes that make sense as a bulk action (not_reachable, callback,
 * dnc, wrong_contact, duplicate, cold_recycled, registered,
 * not_interested). Advanced codes (callback with date) stay on the
 * per-lead modal.
 */
import React, { useState } from "react";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import { toast } from "sonner";
import {
    X, ListChecks, PhoneSlash, Snowflake, GitFork,
    Prohibit, Warning, ThumbsUp, ClockCounterClockwise, Lightning,
} from "@phosphor-icons/react";

// V2.1.1 iter183 (post-testing) — bulk codes must match the backend
// CRM_LEAD_CODES allow-list. Candidate-only codes (not_interested,
// cold_recycled, hired_elsewhere, offer_pending_elsewhere, etc.) are
// intentionally NOT here — they only apply once a lead has been
// promoted into the Kanban.
const BULK_CODES = [
    { code: "reachable",           label: "Reachable",           icon: ThumbsUp,             tone: "emerald" },
    { code: "not_reachable",       label: "Not reachable",       icon: PhoneSlash,           tone: "amber" },
    { code: "callback_scheduled",  label: "Callback",            icon: ClockCounterClockwise, tone: "cobalt" },
    { code: "magic_link_sent",     label: "Magic link sent",     icon: Lightning,            tone: "cobalt" },
    { code: "registered",          label: "Registered",          icon: ThumbsUp,             tone: "emerald",
      hint: "Auto-promotes each lead to a candidate" },
    { code: "wrong_contact",       label: "Wrong contact",       icon: Warning,              tone: "neutral" },
    { code: "duplicate",           label: "Duplicate",           icon: GitFork,              tone: "neutral" },
    { code: "dnc",                 label: "Do not contact",      icon: Prohibit,             tone: "neutral" },
];

const TONE = {
    emerald: "border-emerald-500 hover:bg-emerald-50 text-emerald-800",
    cobalt:  "border-cobalt      hover:bg-cobalt/5   text-cobalt",
    amber:   "border-amber-500   hover:bg-amber-50   text-amber-800",
    rose:    "border-rose-500    hover:bg-rose-50    text-rose-800",
    neutral: "border-neutral-400 hover:bg-neutral-50 text-neutral-700",
};

export default function BulkDispositionModal({ open, leadIds, onClose, onDone }) {
    const [picked, setPicked] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    if (!open) return null;

    async function submit() {
        if (!picked) {
            toast.error("Pick a disposition first");
            return;
        }
        setSaving(true);
        try {
            const r = await api.post("/crm/bulk/leads/disposition", {
                lead_ids: leadIds,
                code: picked,
                reason_notes: notes.trim() || null,
            });
            const promoted = r.data?.auto_promoted_count || 0;
            toast.success(
                `Disposed ${r.data?.disposed || 0} lead(s)`
                + (promoted ? ` · ${promoted} auto-promoted` : ""),
            );
            setPicked(""); setNotes("");
            onDone?.();
        } catch (e) {
            logError("bulk disposition failed", e);
            toast.error(e?.response?.data?.detail || "Bulk disposition failed");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={onClose}
            data-testid="crm-bulk-disposition-modal"
        >
            <div
                className="w-full max-w-lg bg-white border-2 border-ink p-5"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                            Bulk action · {leadIds.length} lead{leadIds.length === 1 ? "" : "s"}
                        </p>
                        <h3 className="font-heading font-black text-lg mt-1 flex items-center gap-2">
                            <ListChecks size={18} weight="bold" /> Bulk disposition
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="p-1 hover:bg-neutral-100"
                        data-testid="crm-bulk-disposition-close"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-4">
                    {BULK_CODES.map((c) => {
                        const Icon = c.icon;
                        const active = picked === c.code;
                        return (
                            <button
                                key={c.code}
                                data-testid={`crm-bulk-code-${c.code}`}
                                onClick={() => setPicked(c.code)}
                                className={`border-2 ${TONE[c.tone]} p-3 text-left transition ${active ? "ring-2 ring-ink bg-neutral-50" : ""}`}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <Icon size={16} weight="bold" />
                                    <span className="text-sm font-bold">{c.label}</span>
                                </div>
                                {c.hint && (
                                    <p className="text-[10px] text-neutral-600">{c.hint}</p>
                                )}
                            </button>
                        );
                    })}
                </div>

                <label className="block mb-4">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                        Reason / notes (optional)
                    </span>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                        data-testid="crm-bulk-disposition-notes"
                        className="mt-1 w-full border-2 border-ink px-3 py-2 text-sm"
                        placeholder="Applied to all selected leads"
                    />
                </label>

                <div className="flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="border-2 border-ink px-4 py-2 text-xs font-bold uppercase"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        disabled={saving || !picked}
                        data-testid="crm-bulk-disposition-submit"
                        className="bg-ink text-white px-4 py-2 text-xs font-bold uppercase disabled:opacity-40"
                    >
                        {saving ? "Applying…" : `Apply to ${leadIds.length}`}
                    </button>
                </div>
            </div>
        </div>
    );
}
